/**
 * inkInWaterPipeline — ink dispersing in water.
 *
 * A center source emits ink that diffuses outward via FBM. Multiple
 * plumes spread and twist. Audio drives the source intensity.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface InkInWaterPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Background: light water with subtle gradient.
  vec3 bg = vec3(0.92, 0.95, 0.97);
  bg -= 0.05 * p.y;
  vec3 col = bg;

  // Multiple ink plumes emanating from the center.
  const int N_PLUMES = 5;
  for (int i = 0; i < N_PLUMES; i++) {
    float fi = float(i);
    // Source point: offset from center.
    float ang = fi * 6.2831853 / float(N_PLUMES) + uTime * 0.1;
    vec2 source = vec2(cos(ang), sin(ang)) * 0.05;
    // Plume: ink spreads along the source-to-pixel direction with
    // FBM distortion.
    vec2 toP = p - source;
    float dist = length(toP);
    float plumeAge = dist * (2.0 - bass * 0.5);  // newer = closer
    // Domain-warped noise for plume shape.
    vec2 nP = toP + vec2(
      fbm(toP * 1.5 + uTime * 0.3) - 0.5,
      fbm(toP * 1.5 + uTime * 0.3 + 3.7) - 0.5
    ) * 0.2;
    float density = exp(-dot(nP, nP) * 4.0) * exp(-plumeAge * 0.5);
    // Audio: source strength.
    density *= 0.5 + bass * 0.5;
    // Color per plume: black ink, red, blue, green, purple.
    vec3 inkColor;
    if (i == 0) inkColor = vec3(0.10, 0.10, 0.15);
    else if (i == 1) inkColor = vec3(0.65, 0.20, 0.10);
    else if (i == 2) inkColor = vec3(0.10, 0.25, 0.65);
    else if (i == 3) inkColor = vec3(0.20, 0.55, 0.20);
    else inkColor = vec3(0.40, 0.15, 0.55);
    col = mix(col, inkColor, clamp(density, 0.0, 1.0));
  }

  // Add a center pool.
  float poolDensity = exp(-length(p) * 4.0) * (0.5 + bass * 0.5);
  col = mix(col, vec3(0.10, 0.10, 0.15), poolDensity);

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.2;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = col / (1.0 + col);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 1.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeInkInWaterBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeInkInWaterRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createInkInWaterPipeline(gl: WebGL2RenderingContext): InkInWaterPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawInkInWater(
  gl: WebGL2RenderingContext,
  pipe: InkInWaterPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
